import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UnderlinedTabs, UnderlinedTabsContent, UnderlinedTabsList, UnderlinedTabsTrigger } from "@/components/ui/underlined-tabs";
import { useToast } from "@/hooks/use-toast";
import { managementCompanyApi, pricingTableApi, documentTypeApi } from "@/services/api";
import { financialAccountApi } from "@/services/api/financialAccountApi";
import OperatingHoursTab from "@/components/management-companies/OperatingHoursTab";
import { Loader2, ArrowLeft } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DocumentGroupSelector } from "@/components/associations/DocumentGroupSelector";
import { DocumentGroup } from "@/models/DocumentGroup";
import { ManagementCompany } from "@/models/ManagementCompany";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip_code: z.string().optional(),
  default_pricing_table_id: z.number().nullable().optional(),
  default_document_groups: z.array(z.number()).optional(),
  // Financial information
  account_holder_name: z.string().optional(),
  bank_name: z.string().optional(),
  account_number: z.string().optional(),
  routing_number: z.string().optional(),
  account_type: z.string().optional(),
  // Address fields for payout account
  street_address: z.string().optional(),
  payout_city: z.string().optional(),
  state_province: z.string().optional(),
  postcode: z.string().optional(),
  wire_instructions: z.string().optional(),
  finance_notes: z.string().optional(),
  payment_schedule_day: z.number().min(1).max(28).optional(),
  // Notification settings
  notification_settings: z.array(z.union([
    z.object({
      notification_type: z.literal('document'),
      document_type_id: z.number(),
      enabled: z.boolean(),
      email: z.string().email().optional().or(z.literal("")),
    }),
    z.object({
      notification_type: z.literal('finance'),
      enabled: z.boolean(),
      email: z.string().email().optional().or(z.literal("")),
    }),
  ])).optional(),
  ai_settings: z.object({
    document_training_enabled: z.boolean(),
    order_analysis_enabled: z.boolean(),
  }).optional(),
});

type FormValues = z.infer<typeof formSchema>;

const ManagementCompanyEditContent = () => {
  const navigate = useNavigate();
  const { id: companyId } = useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("information");
  const [useSameAddress, setUseSameAddress] = useState(true);
  const { selectedRole, hasPermission, getCurrentRoleIds, permissionsLoaded, userRoles } = useAuth();
  const isEditing = !!companyId;

  // Check permissions - use useMemo to recalculate when permissions load
  const canWriteManagementCompanies = useMemo(() => {
    if (!permissionsLoaded) return false;
    return hasPermission(getCurrentRoleIds(), 'write', 'management_companies');
  }, [permissionsLoaded, hasPermission, getCurrentRoleIds]);

  const canCreateManagementCompany = useMemo(() => {
    if (!permissionsLoaded) return false;
    return userRoles.platform.length > 0 &&
      selectedRole?.type === 'platform' &&
      canWriteManagementCompanies;
  }, [permissionsLoaded, userRoles.platform, selectedRole, canWriteManagementCompanies]);

  // Redirect if user doesn't have permission
  // Wait for both permissions and selectedRole to load before checking
  useEffect(() => {
    if (permissionsLoaded && selectedRole) {
      if (isEditing && !canWriteManagementCompanies) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to edit management companies.",
          variant: "destructive",
        });
        navigate("/backoffice/management-companies");
      } else if (!isEditing && !canCreateManagementCompany) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to create management companies.",
          variant: "destructive",
        });
        navigate("/backoffice/management-companies");
      }
    }
  }, [permissionsLoaded, selectedRole, isEditing, canWriteManagementCompanies, canCreateManagementCompany, navigate, toast]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      default_pricing_table_id: null,
      default_document_groups: [],
      account_holder_name: "",
      bank_name: "",
      account_number: "",
      routing_number: "",
      account_type: "",
      street_address: "",
      payout_city: "",
      state_province: "",
      postcode: "",
      wire_instructions: "",
      finance_notes: "",
      payment_schedule_day: undefined, // Optional - if not set, automatic payments are disabled
      notification_settings: [],
      ai_settings: { document_training_enabled: false, order_analysis_enabled: false },
    },
  });

  const { data: managementCompany, isLoading } = useQuery({
    queryKey: ["management-company", companyId],
    queryFn: () => {
      if (!companyId) {
        throw new Error("Management company ID is required");
      }
      return managementCompanyApi.getById(parseInt(companyId));
    },
    enabled: isEditing,
  });

  const { data: pricingTables = [] } = useQuery({
    queryKey: ['pricing-tables', selectedRole?.entityId, { management_company_id: companyId }],
    queryFn: () => {
      if (isEditing && !companyId) {
        throw new Error('Management company ID is required');
      }
      return pricingTableApi.list({ 
        management_company_id: companyId ? parseInt(companyId) : undefined
      });
    },
    enabled: true // Always load pricing tables
  });

  const { data: documentTypes = [] } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => documentTypeApi.list(),
  });

  // Fetch financial accounts for this management company
  const { data: financialAccounts = [] } = useQuery({
    queryKey: ['financialAccounts', companyId],
    queryFn: () => financialAccountApi.getAccounts({
      account_type: 'management_company',
      include_balances: false
    }),
    enabled: isEditing && !!companyId,
  });

  // Get the financial account for this specific management company
  const managementCompanyFinancialAccount = financialAccounts.find(
    (account: any) => account.reference === companyId
  );

  const payoutAccount = managementCompanyFinancialAccount?.payout_accounts?.[0];

  // Use useEffect to reset form when data is loaded
  useEffect(() => {
    if (managementCompany && isEditing && Array.isArray(documentTypes)) {
      // Initialize notification settings for all document types
      // Handle both array format and object format from database
      const existingSettings = Array.isArray(managementCompany.notification_settings)
        ? managementCompany.notification_settings
        : [];

      // Find existing finance notification or create default
      const existingFinanceNotification = existingSettings.find(
        (setting: any) => setting.notification_type === 'finance'
      ) as { notification_type: 'finance'; enabled: boolean; email?: string } | undefined;

      // Document notifications (migrate old format to new format)
      const documentNotifications = documentTypes.map(docType => {
        const existingSetting = existingSettings.find(
          (setting: any) => 'document_type_id' in setting && setting.document_type_id === docType.id
        ) as { document_type_id: number; enabled: boolean; email?: string } | undefined;
        return {
          notification_type: 'document' as const,
          document_type_id: docType.id,
          enabled: existingSetting?.enabled || false,
          email: existingSetting?.email || "",
        };
      });

      // Finance notification
      const financeNotification = {
        notification_type: 'finance' as const,
        enabled: existingFinanceNotification?.enabled || false,
        email: existingFinanceNotification?.email || "",
      };

      const notificationSettings = [...documentNotifications, financeNotification];

      form.reset({
        name: managementCompany.name || "",
        email: managementCompany.email || "",
        phone: managementCompany.phone || "",
        address: managementCompany.address || "",
        city: managementCompany.city || "",
        state: managementCompany.state || "",
        zip_code: managementCompany.zip_code || "",
        default_pricing_table_id: managementCompany.default_pricing_table_id || null,
        default_document_groups: managementCompany.default_document_groups || [],
        // Load financial data from payout_accounts only
        account_holder_name: payoutAccount?.account_holder_name || managementCompany.name || "",
        bank_name: payoutAccount?.bank_name || "",
        account_number: payoutAccount?.bank_account_number || "",
        routing_number: payoutAccount?.bank_routing_number || "",
        account_type: payoutAccount?.bank_account_type || "",
        street_address: payoutAccount?.street_address || "",
        payout_city: payoutAccount?.city || "",
        state_province: payoutAccount?.state_province || "",
        postcode: payoutAccount?.postcode || "",
        wire_instructions: managementCompany.wire_instructions || "",
        finance_notes: managementCompany.finance_notes || "",
        payment_schedule_day: managementCompany.payment_schedule_day || undefined,
        notification_settings: notificationSettings,
        ai_settings: managementCompany.ai_settings || { document_training_enabled: false, order_analysis_enabled: false },
      });
    } else if (!isEditing && Array.isArray(documentTypes) && documentTypes.length > 0) {
      // For new companies, initialize notification settings for all document types
      const documentNotifications = documentTypes.map(docType => ({
        notification_type: 'document' as const,
        document_type_id: docType.id,
        enabled: false,
        email: "",
      }));

      const financeNotification = {
        notification_type: 'finance' as const,
        enabled: false,
        email: "",
      };

      form.setValue("notification_settings", [...documentNotifications, financeNotification]);
    }
  }, [managementCompany, documentTypes, form, isEditing, payoutAccount]);

  // Watch company address fields
  const address = form.watch("address");
  const city = form.watch("city");
  const state = form.watch("state");
  const zipCode = form.watch("zip_code");

  // Sync payout address with company address when checkbox is checked
  useEffect(() => {
    if (useSameAddress) {
      form.setValue("street_address", address || "");
      form.setValue("payout_city", city || "");
      form.setValue("state_province", state || "");
      form.setValue("postcode", zipCode || "");
    }
  }, [useSameAddress, address, city, state, zipCode, form]);

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      // Separate financial data from management company data
      const { account_holder_name, bank_name, account_number, routing_number, account_type, street_address, payout_city, state_province, postcode, ...managementCompanyData } = data;

      // Ensure notification_settings matches the expected format
      const processedData: Partial<ManagementCompany> = {
        ...managementCompanyData,
        notification_settings: Array.isArray(data.notification_settings)
          ? data.notification_settings.filter(setting =>
              setting.document_type_id && typeof setting.enabled === 'boolean'
            ).map(setting => ({
              document_type_id: setting.document_type_id,
              enabled: setting.enabled,
              email: setting.email || undefined,
            }))
          : []
      };

      let savedCompany;
      if (isEditing) {
        if (!companyId) {
          throw new Error("Management company ID is required");
        }
        savedCompany = await managementCompanyApi.update(parseInt(companyId), processedData);
      } else {
        savedCompany = await managementCompanyApi.create(processedData);
      }

      // Save or update payout account data if financial information was provided
      if (bank_name || account_number || routing_number || account_type) {
        const mcId = savedCompany.id;

        // Check if financial account exists for this management company
        let financialAccount = managementCompanyFinancialAccount;

        if (!financialAccount) {
          // Create financial account for this management company
          const accountsData = await financialAccountApi.getAccounts({
            account_type: 'management_company',
            include_balances: false
          });
          financialAccount = accountsData.find((acc: any) => acc.reference === String(mcId));
        }

        if (financialAccount) {
          // Update or create payout account
          const payoutData = {
            payout_method: 'bank_account' as const,
            account_holder_name: account_holder_name || savedCompany.name,
            bank_name: bank_name || '',
            bank_account_number: account_number || '',
            bank_routing_number: routing_number || '',
            bank_account_type: account_type || 'checking',
            street_address: street_address || '',
            city: payout_city || '',
            state_province: state_province || '',
            postcode: postcode || '',
            is_default: true,
          };

          if (payoutAccount) {
            // Update existing payout account - API endpoint needed
            console.log('Payout account update not yet implemented, data:', payoutData);
          } else {
            // Create new payout account
            await financialAccountApi.createPayoutAccount(financialAccount.id, payoutData);
          }
        }
      }

      return savedCompany;
    },
    onSuccess: () => {
      toast({
        title: isEditing 
          ? "Management company updated successfully!" 
          : "Management company created successfully!",
      });
      if (isEditing) {
        queryClient.invalidateQueries({ queryKey: ["management-company", companyId] });
      }
      queryClient.invalidateQueries({ queryKey: ["management-companies"] });
      navigate("/backoffice/management-companies");
    },
    onError: (error: any) => {
      toast({
        title: "Something went wrong.",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  async function onSubmit(values: FormValues) {
    setIsSubmitting(true);
    try {
      await mutation.mutateAsync(values);
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleSelectDocumentGroup = (documentGroup: DocumentGroup) => {
    const currentGroups = form.getValues("default_document_groups") || [];
    if (!Array.isArray(currentGroups)) {
      console.warn("default_document_groups is not an array:", currentGroups);
      return;
    }
    if (!currentGroups.includes(documentGroup.id)) {
      form.setValue("default_document_groups", [...currentGroups, documentGroup.id]);
    }
  };

  const handleRemoveDocumentGroup = (groupId: number) => {
    const currentGroups = form.getValues("default_document_groups") || [];
    if (!Array.isArray(currentGroups)) {
      console.warn("default_document_groups is not an array:", currentGroups);
      return;
    }
    form.setValue(
      "default_document_groups",
      currentGroups.filter((id) => id !== groupId)
    );
  };

  const getPricingTableName = (id: number | null) => {
    if (!id) return "None (Use Default)";
    const table = Array.isArray(pricingTables) ? pricingTables.find((t) => t.id === id) : undefined;
    return table ? table.name : "Unknown";
  };

  const handleUpdateNotificationSetting = (documentTypeId: number, field: 'enabled' | 'email', value: boolean | string) => {
    const currentSettings = form.getValues("notification_settings");
    const settingsArray = Array.isArray(currentSettings) ? currentSettings : [];

    const updatedSettings = settingsArray.map(setting => {
      if ('document_type_id' in setting && setting.document_type_id === documentTypeId) {
        return { ...setting, [field]: value };
      }
      return setting;
    });
    form.setValue("notification_settings", updatedSettings);
  };

  const handleUpdateFinanceNotification = (field: 'enabled' | 'email', value: boolean | string) => {
    const currentSettings = form.getValues("notification_settings");
    const settingsArray = Array.isArray(currentSettings) ? currentSettings : [];

    const updatedSettings = settingsArray.map(setting => {
      if (setting.notification_type === 'finance') {
        return { ...setting, [field]: value };
      }
      return setting;
    });
    form.setValue("notification_settings", updatedSettings);
  };

  const getNotificationSetting = (documentTypeId: number) => {
    const settings = form.watch("notification_settings");
    // Handle both array and non-array cases
    const settingsArray = Array.isArray(settings) ? settings : [];
    const setting = settingsArray.find(s => 'document_type_id' in s && s.document_type_id === documentTypeId);
    if (setting && 'document_type_id' in setting) {
      return setting;
    }
    return {
      notification_type: 'document' as const,
      document_type_id: documentTypeId,
      enabled: false,
      email: "",
    };
  };

  const getFinanceNotification = () => {
    const settings = form.watch("notification_settings");
    const settingsArray = Array.isArray(settings) ? settings : [];
    const setting = settingsArray.find(s => s.notification_type === 'finance');
    if (setting && setting.notification_type === 'finance') {
      return setting;
    }
    return {
      notification_type: 'finance' as const,
      enabled: false,
      email: "",
    };
  };

  if (isLoading && isEditing) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        </div>
      </BackofficeLayout>
    );
  }

  // Show loading while permissions are being checked
  if (!permissionsLoaded) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        </div>
      </BackofficeLayout>
    );
  }

  // Check access permissions after permissions are loaded
  if (isEditing && !canWriteManagementCompanies) {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-10">
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-xl mb-2">Access Denied</div>
            <div className="text-muted-foreground mb-4">You don't have permission to edit management companies.</div>
            <Button asChild>
              <Link to="/backoffice/management-companies">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Management Companies
              </Link>
            </Button>
          </div>
        </div>
      </BackofficeLayout>
    );
  }

  if (!isEditing && !canCreateManagementCompany) {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-10">
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-xl mb-2">Access Denied</div>
            <div className="text-muted-foreground mb-4">You don't have permission to create management companies.</div>
            <Button asChild>
              <Link to="/backoffice/management-companies">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Management Companies
              </Link>
            </Button>
          </div>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">
            {isEditing 
              ? (managementCompany ? `Edit ${managementCompany.name}` : "Edit Management Company")
              : "Create New Management Company"
            }
          </h1>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate("/backoffice/management-companies")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Management Companies
            </Button>
          </div>
        </div>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <UnderlinedTabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <UnderlinedTabsList>
                <UnderlinedTabsTrigger value="information">Information</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="document-groups">Document Groups</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="document-types">Document Types</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="finance">Finance</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="operating-hours">Operating Hours</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="notifications">Notifications</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="ai">AI</UnderlinedTabsTrigger>
              </UnderlinedTabsList>

              <UnderlinedTabsContent value="information" className="space-y-4">
                <div className="rounded-md border bg-muted p-6 space-y-6">
                  <h2 className="text-xl font-semibold">Basic Information</h2>
                  
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Management Company Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="company@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input placeholder="(555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Input placeholder="Street Address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input placeholder="City" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <FormControl>
                            <Input placeholder="State" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="zip_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ZIP Code</FormLabel>
                          <FormControl>
                            <Input placeholder="ZIP Code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="default_pricing_table_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Pricing Table</FormLabel>
                        <Select
                          value={field.value?.toString() ?? "none"}
                          onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue>
                                {field.value ? getPricingTableName(field.value) : "None (Use System Default)"}
                              </SelectValue>
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">None (Use System Default)</SelectItem>
                            {Array.isArray(pricingTables) && pricingTables.map((table) => (
                              <SelectItem key={table.id} value={table.id.toString()}>
                                {table.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Select a default pricing table for new associations under this management company
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </UnderlinedTabsContent>

              <UnderlinedTabsContent value="document-groups" className="space-y-4">
                <div className="rounded-md border bg-muted p-6 space-y-6">
                  <h2 className="text-xl font-semibold">Default Document Groups</h2>
                  <p className="text-sm text-muted-foreground">
                    These document groups will be automatically assigned to new associations created under this management company.
                  </p>
                  
                  <div className="space-y-2">
                    <FormLabel>Document Groups</FormLabel>
                    <DocumentGroupSelector
                      selectedGroups={form.watch("default_document_groups") || []}
                      onSelectGroup={handleSelectDocumentGroup}
                      onRemoveGroup={handleRemoveDocumentGroup}
                      managementCompanyId={companyId ? parseInt(companyId) : undefined}
                    />
                    <FormDescription>
                      Document groups determine what documents are available for associations under this management company
                    </FormDescription>
                  </div>
                </div>
              </UnderlinedTabsContent>

              {/* The company's own document types live HERE (8-Sep: in a MC
                  context there is no Settings — "lo podés editar desde acá
                  adentro"). Platform types stay the platform's. */}
              <UnderlinedTabsContent value="document-types" className="space-y-4">
                <div className="rounded-md border bg-muted p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold">Document Types</h2>
                      <p className="text-sm text-muted-foreground">
                        This company's own document types. Platform types are
                        shared with every company and managed by the platform.
                      </p>
                    </div>
                    <Button asChild type="button">
                      <Link to={`/backoffice/document-types/new?mc=${companyId}`}>
                        New Document Type
                      </Link>
                    </Button>
                  </div>
                  {(documentTypes as any[]).filter(
                    (t) =>
                      Number((t as any).management_company_id) ===
                      Number(companyId)
                  ).length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No own document types yet — this company uses the
                      platform ones.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead className="w-[100px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(documentTypes as any[])
                          .filter(
                            (t) =>
                              Number((t as any).management_company_id) ===
                              Number(companyId)
                          )
                          .map((t: any) => (
                            <TableRow key={t.id}>
                              <TableCell className="font-medium">
                                {t.name}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  asChild
                                  type="button"
                                >
                                  <Link
                                    to={`/backoffice/document-types/${t.id}`}
                                  >
                                    Edit
                                  </Link>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </UnderlinedTabsContent>

              <UnderlinedTabsContent value="finance" className="space-y-4">
                <div className="rounded-md border bg-muted p-6 space-y-6">
                  <h2 className="text-xl font-semibold">Financial Information</h2>

                  <FormField
                    control={form.control}
                    name="account_holder_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Holder Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Account Holder Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="bank_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bank Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Bank Name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="account_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bank Account Type</FormLabel>
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select account type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="checking">Checking</SelectItem>
                              <SelectItem value="savings">Savings</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="routing_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Routing Number</FormLabel>
                          <FormControl>
                            <Input placeholder="Routing Number" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="account_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Account Number</FormLabel>
                          <FormControl>
                            <Input placeholder="Account Number" type="password" {...field} />
                          </FormControl>
                          <FormDescription>
                            Account number is masked for security
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex items-center space-x-2 mb-4 mt-6">
                    <Checkbox
                      id="useSameAddress"
                      checked={useSameAddress}
                      onCheckedChange={(checked) => setUseSameAddress(checked === true)}
                    />
                    <Label
                      htmlFor="useSameAddress"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Use same address as company address for payout account
                    </Label>
                  </div>

                  <h3 className="text-lg font-semibold">Payout Account Address</h3>

                  <FormField
                    control={form.control}
                    name="street_address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Address</FormLabel>
                        <FormControl>
                          <Input placeholder="Street Address" {...field} disabled={useSameAddress} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="payout_city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input placeholder="City" {...field} disabled={useSameAddress} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state_province"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State / Province</FormLabel>
                          <FormControl>
                            <Input placeholder="State / Province" {...field} disabled={useSameAddress} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="postcode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postal Code</FormLabel>
                        <FormControl>
                          <Input placeholder="Postal Code" {...field} disabled={useSameAddress} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="payment_schedule_day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Schedule Day (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={28}
                            placeholder="e.g., 15 (leave empty to disable automatic payments)"
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          />
                        </FormControl>
                        <FormDescription>
                          Day of the month (1-28) when automatic batch payments are processed for all associations under this management company. If empty, automatic payment processing is disabled and funds must be released manually.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </UnderlinedTabsContent>

              <UnderlinedTabsContent value="operating-hours" className="space-y-4">
                {companyId && (
                  <OperatingHoursTab managementCompanyId={Number(companyId)} />
                )}
              </UnderlinedTabsContent>

              <UnderlinedTabsContent value="notifications" className="space-y-4">
                {/* Finance Notifications Section */}
                <div className="rounded-md border bg-muted p-6 space-y-6">
                  <h2 className="text-xl font-semibold">Finance Notifications</h2>
                  <p className="text-sm text-muted-foreground">
                    Configure email notifications for financial withdrawals. When enabled, the specified email address will receive notifications when:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc list-inside ml-4">
                    <li>Withdrawal is in progress (batch file generated at 3am)</li>
                    <li>Withdrawal is completed (wire transfer confirmation received from Airwallex)</li>
                  </ul>

                  <div className="bg-background rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={getFinanceNotification().enabled}
                            onCheckedChange={(checked) =>
                              handleUpdateFinanceNotification('enabled', !!checked)
                            }
                          />
                          <span className="text-sm font-medium">Enable Finance Notifications</span>
                        </div>
                        <div className="flex-1 max-w-md">
                          <Input
                            type="email"
                            placeholder="finance@example.com"
                            value={getFinanceNotification().email || ""}
                            onChange={(e) =>
                              handleUpdateFinanceNotification('email', e.target.value)
                            }
                            disabled={!getFinanceNotification().enabled}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Document Type Notifications Section */}
                <div className="rounded-md border bg-muted p-6 space-y-6">
                  <h2 className="text-xl font-semibold">Document Type Notifications</h2>
                  <p className="text-sm text-muted-foreground">
                    Configure email notifications for different document types. When enabled, the specified email address will receive notifications for documents of that type.
                  </p>

                  <div className="bg-background rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Document Type</TableHead>
                          <TableHead className="text-center">Enable Notifications</TableHead>
                          <TableHead>Email Address</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Array.isArray(documentTypes) && documentTypes.map((docType) => {
                          const setting = getNotificationSetting(docType.id);
                          return (
                            <TableRow key={docType.id}>
                              <TableCell className="font-medium">
                                {docType.name}
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox
                                  checked={setting.enabled}
                                  onCheckedChange={(checked) =>
                                    handleUpdateNotificationSetting(docType.id, 'enabled', !!checked)
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="email"
                                  placeholder="notifications@example.com"
                                  value={setting.email || ""}
                                  onChange={(e) =>
                                    handleUpdateNotificationSetting(docType.id, 'email', e.target.value)
                                  }
                                  disabled={!setting.enabled}
                                  className="max-w-xs"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {Array.isArray(documentTypes) && documentTypes.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No document types available. Create document types first to configure notifications.</p>
                    </div>
                  )}
                </div>
              </UnderlinedTabsContent>

              <UnderlinedTabsContent value="ai" className="space-y-4">
                <div className="rounded-md border bg-muted p-6 space-y-6">
                  <h2 className="text-xl font-semibold">AI Document Training</h2>
                  <p className="text-sm text-muted-foreground">
                    When enabled, uploading document attachments (files) will automatically trigger AI extraction of rules and requirements into a structured knowledge base per association. Expiration dates are tracked - when documents expire, extracted knowledge is marked as outdated.
                  </p>

                  <div className="bg-background rounded-lg border p-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="ai-document-training"
                        checked={form.watch("ai_settings.document_training_enabled")}
                        onCheckedChange={(checked) =>
                          form.setValue("ai_settings.document_training_enabled", !!checked, { shouldDirty: true })
                        }
                      />
                      <label htmlFor="ai-document-training" className="text-sm font-medium cursor-pointer">Enable AI Document Training</label>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border bg-muted p-6 space-y-6">
                  <h2 className="text-xl font-semibold">AI Application Analysis</h2>
                  <p className="text-sm text-muted-foreground">
                    When enabled, the order document detail page will show an AI analysis section. The AI cross-references submitted application data against the association's knowledge base, flagging compliance issues and providing suggestions.
                  </p>

                  <div className="bg-background rounded-lg border p-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="ai-application-analysis"
                        checked={form.watch("ai_settings.order_analysis_enabled")}
                        onCheckedChange={(checked) =>
                          form.setValue("ai_settings.order_analysis_enabled", !!checked, { shouldDirty: true })
                        }
                      />
                      <label htmlFor="ai-application-analysis" className="text-sm font-medium cursor-pointer">Enable AI Application Analysis</label>
                    </div>
                  </div>
                </div>
              </UnderlinedTabsContent>

              <div className="flex justify-end space-x-2 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/backoffice/management-companies")}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isEditing ? "Saving..." : "Creating..."}
                    </>
                  ) : (
                    isEditing ? "Save Changes" : "Create Management Company"
                  )}
                </Button>
              </div>
            </UnderlinedTabs>
          </form>
        </FormProvider>
      </div>
    </BackofficeLayout>
  );
};

// Wrap the component with ErrorBoundary
const ManagementCompanyEdit = () => {
  return (
    <ErrorBoundary>
      <ManagementCompanyEditContent />
    </ErrorBoundary>
  );
};

export default ManagementCompanyEdit;
